
import { useStore } from '@/store'
import { computed, defineComponent, reactive, ref } from 'vue'
import { Orientation } from '@/domain/models/TrinketSettings'
import { Point } from '@/domain/models/Point'
import TrinketGridPoint from '@/components/TrinketGrid/TrinketGridPoint.vue'
import TrinketGridLine from '@/components/TrinketGrid/TrinketGridLine.vue'
import { Tools } from '@/store/state'
import TrinketGridText from '@/components/TrinketGrid/TrinketGridText.vue'

export default defineComponent({
    name: 'TrinketGrid',
    components: {
        TrinketGridText,
        TrinketGridLine,
        TrinketGridPoint
    },
    setup () {
        const store = useStore()
        let fired = false
        const svgContainer = ref()
        const mouseCellPosition = reactive<Point>({
            x: 0,
            y: 0
        })
        const mousePosition = reactive<Point>({
            x: 0,
            y: 0
        })

        const orientation = computed(() => store.state.configuration.grid.orientation)
        const gridWidth = computed(() => store.state.configuration.grid.width)
        const gridHeight = computed(() => store.state.configuration.grid.height)
        const gridHelperColor = computed(() => store.state.configuration.grid.gridHelperColor)
        const gridPointRadius = computed(() => store.state.configuration.point.radius)
        const showGridHelper = computed(() => store.state.configuration.grid.showGridHelper)
        const showGridAxis = computed(() => store.state.configuration.grid.showGridAxis)
        const gridAxisColor = computed(() => store.state.configuration.grid.gridAxisColor)
        const selectedTool = computed(() => store.state.trinketGrid.selectedTool)
        const points = computed(() => store.getters.trinketGridPoints)
        const lines = computed(() => store.getters.trinketGridLines)
        const texts = computed(() => store.getters.trinketGridTexts)
        const currentLine = computed(() => store.getters.trinketGridCurrentLine)

        const handleGlobalKeyUp = () => {
            fired = false
        }
        const handleGlobalKeyDown = (event: KeyboardEvent) => {
            if ((event.key === 's' && (event.ctrlKey || event.metaKey || event.shiftKey)) ||
                (event.key === 'e' && (event.ctrlKey || event.metaKey))) {
                event.preventDefault()
                event.cancelBubble = true
            }

            if (fired) {
                return
            }

            if (document.activeElement?.tagName === 'BODY') {
                switch (event.key) {
                    case 'Escape':
                        if (currentLine.value) {
                            store.dispatch('cancelCurrentLine')
                            fired = true
                        }
                        break
                    case 'n':
                        store.dispatch('newTrinket')
                        break
                    case 'f':
                        store.dispatch('selectTool', Tools.FILLER)
                        fired = true
                        break
                    case 'l':
                        store.dispatch('selectTool', Tools.LINE)
                        fired = true
                        break
                    case 'b':
                        store.dispatch('selectTool', Tools.BORDER)
                        fired = true
                        break
                    case 'p':
                        store.dispatch('selectTool', Tools.PICKER)
                        fired = true
                        break
                    case 's':
                        if (event.ctrlKey || event.metaKey) {
                            store.dispatch('saveTrinket')
                            fired = true
                        }

                        if (event.shiftKey) {
                            store.dispatch('exportTrinket')
                            fired = true
                        }
                        break
                    case 'e':
                        if (event.ctrlKey || event.metaKey) {
                            store.dispatch('selectTool', Tools.LINE_ERASER)
                            fired = true
                        } else {
                            store.dispatch('selectTool', Tools.ERASER)
                            fired = true
                        }
                        break
                    case 't':
                        event.preventDefault()
                        store.dispatch('selectTool', Tools.TEXT)
                        fired = true
                        break
                }
            }
        }

        const calculateGridFromPosition = (): Point => {
            return {
                x: Math.round((mousePosition.x - gridWidth.value / 2) / gridWidth.value),
                y: Math.round((mousePosition.y - gridHeight.value / 2) / gridHeight.value)
            }
        }

        const handleMouseButtons = (cell: Point, buttons: number): void => {
            if (buttons === 1) {
                switch (selectedTool.value) {
                    case Tools.FILLER:
                    case Tools.BORDER:
                        store.dispatch('addPoint', {
                            x: cell.x,
                            y: cell.y
                        })
                        break

                    case Tools.LINE:
                        store.dispatch('addLine', {
                            x: cell.x,
                            y: cell.y
                        })
                        break

                    case Tools.ERASER:
                        store.dispatch('removePoint', {
                            x: cell.x,
                            y: cell.y
                        })
                        break

                    case Tools.LINE_ERASER:
                        store.dispatch('removeLine', {
                            x: cell.x,
                            y: cell.y
                        })
                        break

                    case Tools.TEXT:
                        store.dispatch('addText', {
                            x: cell.x,
                            y: cell.y
                        })
                        break
                }
            }
        }

        const handleMouseClick = () => {
            const cell = calculateGridFromPosition()

            handleMouseButtons(cell, 1)
        }

        const handleTouchMove = (event: MouseEvent) => {
            if (svgContainer.value !== null) {
                mousePosition.x = event.clientX - svgContainer.value.offsetLeft + window.scrollX
                mousePosition.y = event.clientY - svgContainer.value.offsetTop + window.scrollY
            }

            const cell = calculateGridFromPosition()

            if (selectedTool.value === Tools.BORDER || selectedTool.value === Tools.FILLER) {
                handleMouseButtons(cell, event.buttons)
            }

            if (currentLine.value !== undefined) {
                store.dispatch('updateLine', {
                    x: cell.x,
                    y: cell.y
                })
            }

            mouseCellPosition.x = cell.x * gridWidth.value
            mouseCellPosition.y = cell.y * gridHeight.value
        }

        document.addEventListener('keydown', handleGlobalKeyDown, false)
        document.addEventListener('keyup', handleGlobalKeyUp, false)

        return {
            handleMouseClick,
            handleTouchMove,
            orientation,
            gridWidth,
            gridHeight,
            gridHelperColor,
            gridPointRadius,
            showGridHelper,
            showGridAxis,
            gridAxisColor,
            points,
            lines,
            texts,
            currentLine,
            mouseCellPosition,
            svgContainer,
            Orientation
        }
    }
})
